// DeleteModal — sidebar-bound danger modal. Ported from selectors-sidebar.jsx
// (lines 438-514). Renders inside the sidebar's <aside>, so the backdrop
// stays bounded by the panel rather than the page.

import { Slash } from "./Slash";

const Trash = ({ size = 15 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const InfoIcon = ({ size = 12 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="11" x2="12" y2="17" />
    <circle cx="12" cy="7.5" r="0.6" fill="currentColor" />
  </svg>
);

type Props = {
  /** What's being deleted — usually inventory.label_name. */
  itemName: string;
  /** Library item name shown in the explainer body. */
  libraryName?: string;
  /** When > 0 the modal renders an amber callout warning about referencing
   *  formulations. */
  referencingFormulationsCount?: number;
  onCancel: () => void;
  onConfirm: () => void;
};

export function DeleteModal({
  itemName,
  libraryName,
  referencingFormulationsCount = 0,
  onCancel,
  onConfirm,
}: Props) {
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        zIndex: 200,
        background: "rgba(20, 30, 22, 0.42)",
        backdropFilter: "blur(2px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
        animation: "fadeIn 160ms ease",
      }}
    >
      <style>{`
        @keyframes fadeIn { from { opacity: 0 } to { opacity: 1 } }
        @keyframes slideIn {
          from { transform: translateY(8px); opacity: 0 }
          to   { transform: translateY(0);   opacity: 1 }
        }
      `}</style>
      <div
        style={{
          width: "100%",
          maxWidth: 460,
          background: "var(--panel)",
          borderRadius: 12,
          boxShadow: "0 16px 48px -12px rgba(20,30,22,0.32)",
          border: "1px solid var(--line-strong)",
          overflow: "hidden",
          animation: "slideIn 200ms cubic-bezier(0.2,0.7,0.2,1)",
        }}
      >
        <div style={{ padding: "18px 22px 14px" }}>
          <div className="hstack" style={{ gap: 10, marginBottom: 12 }}>
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 999,
                background: "rgba(161, 75, 58, 0.12)",
                color: "#a14b3a",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <Trash size={15} />
            </span>
            <div className="vstack" style={{ gap: 2 }}>
              <Slash>Delete inventory item</Slash>
              <span
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: "var(--ink)",
                  fontFamily: "var(--font-display)",
                  letterSpacing: "-0.005em",
                }}
              >
                Delete{" "}
                <em
                  style={{ fontStyle: "italic", color: "var(--ink-2)" }}
                >
                  "{itemName}"
                </em>
                ?
              </span>
            </div>
          </div>
          <p
            style={{
              margin: 0,
              fontSize: 13,
              color: "var(--ink-3)",
              lineHeight: 1.55,
            }}
          >
            This will remove the lot, its documents and chromatograms.
            {libraryName && (
              <>
                {" "}If this is the only lot of{" "}
                <strong>{libraryName}</strong>, the parent library item will be
                archived too.
              </>
            )}
          </p>
          {referencingFormulationsCount > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: 6,
                background: "var(--signal-soft)",
                border: "1px solid #e7c97a",
                color: "#7c5813",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
                fontSize: 11.5,
                lineHeight: 1.55,
              }}
            >
              <span style={{ flexShrink: 0, marginTop: 2 }}>
                <InfoIcon size={12} />
              </span>
              <span>
                {referencingFormulationsCount} formulation
                {referencingFormulationsCount === 1 ? "" : "s"} reference this
                lot. They'll fall back to the next in-stock lot of{" "}
                {libraryName ?? "this library item"}, or be flagged if none
                remains.
              </span>
            </div>
          )}
        </div>
        <div
          className="hstack"
          style={{
            padding: "12px 22px",
            background: "var(--panel-2)",
            borderTop: "1px solid var(--line)",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button className="btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            onClick={onConfirm}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "6px 14px",
              fontSize: 13,
              background: "#a14b3a",
              color: "#fff",
              border: "1px solid #a14b3a",
              borderRadius: 5,
              cursor: "pointer",
              fontFamily: "inherit",
              fontWeight: 500,
            }}
          >
            <Trash size={12} />
            Delete inventory item
          </button>
        </div>
      </div>
    </div>
  );
}
